import React, { useEffect, useState, useRef } from 'react';
import { Grant } from '../../pages/Grants';

interface AddGrantModalProps {
  visible: boolean;
  initialValues?: Grant;
  onClose: () => void;
  onGrantAdded: () => void;
}

const AddGrantModal: React.FC<AddGrantModalProps> = ({
  visible = false,
  initialValues,
  onClose,
  onGrantAdded,
}) => {
  const [formValues, setFormValues] = useState({
    title: initialValues?.title || '',
    agency: initialValues?.agency || '',
    category: initialValues?.category || '',
    description: initialValues?.description || '',
    url: initialValues?.url || '',
    status: initialValues?.status || '',
    submissionDate: initialValues?.submissionDate || '',
    closeDate: initialValues?.closeDate || '',
    fundingStartDate: initialValues?.fundingStartDate || '',
    fundingEndDate: initialValues?.fundingEndDate || '',
    amountRequested: initialValues?.amountRequested || '',
    amountAwarded: initialValues?.amountAwarded || '',
  });

  const isEdit = !!initialValues;
  const modalRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = async () => {
    const payload = {
      ...formValues,
      submissionDate: formValues.submissionDate
        ? new Date(formValues.submissionDate).toISOString()
        : '',
      closeDate: formValues.closeDate ? new Date(formValues.closeDate).toISOString() : '',
      fundingStartDate: formValues.fundingStartDate
        ? new Date(formValues.fundingStartDate).toISOString()
        : '',
      fundingEndDate: formValues.fundingEndDate
        ? new Date(formValues.fundingEndDate).toISOString()
        : '',
    };
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/grants/${
          isEdit ? `update/${initialValues.grantId}` : 'add'
        }`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add grant');
      }

      setFormValues({
        title: '',
        agency: '',
        category: '',
        description: '',
        url: '',
        status: '',
        submissionDate: '',
        closeDate: '',
        fundingStartDate: '',
        fundingEndDate: '',
        amountRequested: '',
        amountAwarded: '',
      });
      onGrantAdded();
      onClose();
      console.log('Grant successfully logged');
    } catch (error) {
      console.log(error);
      alert('Failed to add grant. Please try again.');
    }
  };

  const formatDate = (date: string | undefined) =>
    date ? new Date(date).toISOString().split('T')[0] : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  useEffect(() => {
    if (visible && formRef.current) {
      formRef.current.scrollTop = 0;
    }
  }, [visible]);

  useEffect(() => {
    if (initialValues) {
      const submissionDate = formatDate(initialValues.submissionDate);
      const closeDate = formatDate(initialValues.closeDate);
      const fundingStartDate = formatDate(initialValues.fundingStartDate);
      const fundingEndDate = formatDate(initialValues.fundingEndDate);

      setFormValues({
        title: initialValues.title,
        agency: initialValues.agency,
        category: initialValues.category,
        description: initialValues.description,
        url: initialValues.url,
        status: initialValues.status,
        submissionDate: submissionDate,
        closeDate: closeDate,
        fundingStartDate: fundingStartDate,
        fundingEndDate: fundingEndDate,
        amountRequested: initialValues.amountRequested,
        amountAwarded: initialValues.amountAwarded,
      });
    } else {
      setFormValues({
        title: '',
        agency: '',
        category: '',
        description: '',
        url: '',
        status: '',
        submissionDate: '',
        closeDate: '',
        fundingStartDate: '',
        fundingEndDate: '',
        amountRequested: '',
        amountAwarded: '',
      });
    }
  }, [initialValues]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${
        visible ? '' : 'hidden'
      }`}
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isEdit ? 'Edit' : 'Add'} Grant</h2>
          <button onClick={onClose}>
            <i className="fa fa-x text-xs" />
          </button>
        </div>
        <form className="overflow-auto max-h-[75vh]">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Title</label>
            <input
              type="text"
              name="title"
              value={formValues.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Agency</label>
            <input
              type="text"
              name="agency"
              value={formValues.agency}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Category</label>
            <input
              type="text"
              name="category"
              value={formValues.category}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              placeholder="Research, Engineering, etc."
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formValues.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal resize-y"
              placeholder="Enter grant description..."
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="url" className="block text-sm font-medium mb-1">
              URL
            </label>
            <input
              type="url"
              name="url"
              value={formValues.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              placeholder="https://example.com"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="status" className="block text-sm font-medium mb-1">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formValues.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            >
              <option value="">Select status</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="awarded">Awarded</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Submission Date</label>
            <input
              type="date"
              name="submissionDate"
              value={formValues.submissionDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Close Date</label>
            <input
              type="date"
              name="closeDate"
              value={formValues.closeDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Funding Start Date</label>
            <input
              type="date"
              name="fundingStartDate"
              value={formValues.fundingStartDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Funding End Date</label>
            <input
              type="date"
              name="fundingEndDate"
              value={formValues.fundingEndDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Amount Requested</label>
            <input
              type="text"
              name="amountRequested"
              value={formValues.amountRequested}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Amount Awarded</label>
            <input
              type="text"
              name="amountAwarded"
              value={formValues.amountAwarded}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-2 px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={`text-sm px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600`}
            >
              Confirm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGrantModal;
